import { Domino, DominoResponse, scenario } from '@toggle/toggle';
import { useEffect, useState } from 'react';

import { fetchMultipleEntities } from '~/api/entities/entity-service';
import { useAPI } from '~/hooks/use-api/useAPI';
import { ScenarioData } from '~/stores/use-chat/assistant-response.type';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import {
  filterPredictions,
  mapDominosByPeriod,
  MappedDominosByPeriod,
} from '~/utils/domino-utils/domino-utils';

export interface UseMappedDominosProps {
  data: ScenarioData;
  shouldMapDominos: boolean;
}

export const useMappedDominos = ({
  data,
  shouldMapDominos,
}: UseMappedDominosProps) => {
  const [entitiesError, setEntitiesError] = useState(false);
  const [mappedDominosByPeriod, setMappedDominosByPeriod] =
    useState<MappedDominosByPeriod>();

  const {
    data: dominoResponse,
    loading,
    error: dominoError,
  } = useAPI(
    signal =>
      apiFetch<DominoResponse>(scenario.domino.path, {
        method: 'post',
        signal,
        body: {
          ...data,
        },
      }),
    { deps: [data.entities] }
  );

  useEffect(() => {
    if (!dominoResponse || !shouldMapDominos) {
      return undefined;
    }

    const fetchEntityAndMapDominos = async (
      dominos: Domino[],
      abortSignal: AbortSignal
    ) => {
      try {
        const entityTags = data.entities.map(entity => entity.tag);

        const validEntities = await fetchMultipleEntities(
          entityTags,
          abortSignal
        );

        const mappedDominosByPeriod = mapDominosByPeriod(
          dominos,
          validEntities
        );

        setMappedDominosByPeriod(mappedDominosByPeriod);
      } catch (error) {
        setEntitiesError(true);
      }
    };

    const abortController = new AbortController();
    fetchEntityAndMapDominos(dominoResponse, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [data.entities, dominoResponse]);

  const filteredDominos = filterPredictions(dominoResponse);

  return {
    loading,
    error: dominoError || entitiesError,
    dominos: filteredDominos,
    mappedDominosByPeriod,
  };
};
